import { create } from 'zustand';
import { api } from '@/api/client';
import type { AuthUser } from '@pulse/shared';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, totpCode?: string) => Promise<{ requiresTwoFactor: boolean }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (email, password, totpCode) => {
    set({ error: null });

    try {
      const response = await api.post<{
        user: AuthUser;
        requiresTwoFactor: boolean;
      }>('/auth/login', { email, password, totpCode });

      if (response.requiresTwoFactor) {
        return { requiresTwoFactor: true };
      }

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });

      return { requiresTwoFactor: false };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ error: message });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      // Ignore errors on logout
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  checkAuth: async () => {
    try {
      const response = await api.get<{ user: AuthUser }>('/auth/me');
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  updateUser: (user) => set({ user }),

  clearError: () => set({ error: null }),
}));

// Check auth on app load
useAuthStore.getState().checkAuth();
